import { AxiosError } from "axios";
import { useMutation, useQuery } from "react-query";
import HttpService from "../../services/http";
import publicHttp from "../../services/publicHttp";
import type { AgendaMember, Appointment, PaymentCurrency } from "./index";

/** Cents, in the type's currency. */
export interface BookingPrice {
  amount: number;
  currency: PaymentCurrency;
}

/** A booking as the tunnel gets it: `payment_link` for a paid type, `ics_url` once confirmed. */
export type BookedAppointment = Appointment & {
  payment_link?: string | null;
  ics_url?: string | null;
};

export interface BookingSlot {
  start: number;
  end: number;
}

export type BookingRefusal =
  | "SLOT_TAKEN"
  | "TOO_MANY_UPCOMING"
  | "OUTSIDE_HOURS"
  | "MEMBER_NOT_OFFERED"
  | "TOO_LATE"
  | "TOO_FAR"
  | "PAYMENT_UNAVAILABLE";

export const bookingRefusalOf = (
  error: AxiosError<any> | null | undefined
): BookingRefusal | undefined => error?.response?.data?.element?.reason;

export interface PublicBookingType {
  entity: { name: string; logo: string | null };
  type: {
    id: string;
    title: string;
    duration_min: number;
    min_notice_hours: number;
    horizon_days: number;
    description?: string;
    location?: string;
    image_url?: string | null;
    price?: BookingPrice | null;
  };
  members: AgendaMember[];
  login_url: string;
  signup_url: string | null;
}

export interface PublicBookingPage {
  entity: { name: string; logo: string | null };
  types: {
    id: string;
    title: string;
    location: string;
    duration_min: number;
    description: string;
    image_url: string | null;
    price?: BookingPrice | null;
  }[];
  /** Asked for one person: their name as clients see it, null once the entity no longer has them. */
  member?: { user_identification: string; name: string | null } | null;
}

const typeBase = (entityId: string, typeId: string) =>
  `/${entityId}/agenda/types/${typeId}`;

/**
 * The active types the entity lists on its booking page, in its order, only those offering `member` when given;
 * 404 while its agenda is off.
 */
export const usePublicBookingPage = (
  entityId?: string,
  member?: string | null,
  { enabled = true }: { enabled?: boolean } = {}
) =>
  useQuery<PublicBookingPage, AxiosError<any>>(
    ["public-booking-page", entityId, member ?? ""],
    async () => {
      const res = await publicHttp.get(`/${entityId}/agenda/types`, {
        params: member ? { member } : undefined,
      });
      return res?.data?.element;
    },
    {
      enabled: enabled && !!entityId,
      retry: false,
      refetchOnWindowFocus: false,
    }
  );

export const usePublicBookingType = (entityId?: string, typeId?: string) =>
  useQuery<PublicBookingType, AxiosError<any>>(
    ["public-booking-type", entityId, typeId],
    async () => {
      const res = await publicHttp.get(
        typeBase(entityId as string, typeId as string)
      );
      return res?.data?.element;
    },
    {
      enabled: !!entityId && !!typeId,
      retry: false,
      refetchOnWindowFocus: false,
    }
  );

export const usePublicBookingSlots = ({
  entityId,
  typeId,
  member,
  enabled = true,
}: {
  entityId?: string;
  typeId?: string;
  member?: string;
  enabled?: boolean;
}) =>
  useQuery<BookingSlot[], AxiosError<any>>(
    ["public-booking-slots", entityId, typeId, member ?? ""],
    async () => {
      const res = await publicHttp.get(
        `${typeBase(entityId as string, typeId as string)}/slots`,
        { params: { member } }
      );
      return res?.data?.element?.slots ?? [];
    },
    {
      enabled: enabled && !!entityId && !!typeId && !!member,
      retry: false,
      refetchOnWindowFocus: false,
    }
  );

export const useBookPublicSlot = (entityId?: string, typeId?: string) =>
  useMutation<
    BookedAppointment,
    AxiosError<any>,
    {
      userId: string;
      loginToken: string;
      member: string;
      start: number;
      lang: string;
      fromForm?: string | null;
    }
  >(async ({ userId, loginToken, member, start, lang, fromForm }) => {
    const res = await publicHttp.post(
      `${typeBase(entityId as string, typeId as string)}/book/${userId}`,
      { member, start, lang, ...(fromForm ? { from_form: fromForm } : {}) },
      { headers: { "x-login-token": loginToken } }
    );
    return res?.data?.element;
  });

/** What the payment return answers besides 200; the last three end the booking. */
export type PaymentReturnState =
  | "PAYMENT_PENDING"
  | "PAYMENT_DUE"
  | "HOLD_EXPIRED"
  | "SLOT_TAKEN_AFTER_PAYMENT"
  | "BOOKING_CANCELLED";

export interface PaymentReturnAnswer {
  appointment: BookedAppointment | null;
  payment_link: string | null;
}

export const paymentReturnOf = (
  error: AxiosError<any> | null | undefined
): { state: PaymentReturnState; answer: PaymentReturnAnswer } | null => {
  const code = error?.response?.data?.code;
  return [
    "PAYMENT_PENDING",
    "PAYMENT_DUE",
    "HOLD_EXPIRED",
    "SLOT_TAKEN_AFTER_PAYMENT",
    "BOOKING_CANCELLED",
  ].includes(code)
    ? {
        state: code,
        answer: {
          appointment: error?.response?.data?.element?.appointment ?? null,
          payment_link: error?.response?.data?.element?.payment_link ?? null,
        },
      }
    : null;
};

/** The client back from paying: the signature of the return link stands for the login, for this booking only. */
export const useConfirmBookingPayment = (entityId?: string, typeId?: string) =>
  useMutation<
    BookedAppointment,
    AxiosError<any>,
    { userId: string; id: string; sig: string }
  >(async ({ userId, id, sig }) => {
    const res = await publicHttp.post(
      `${typeBase(
        entityId as string,
        typeId as string
      )}/book/${userId}/confirm`,
      { id, sig }
    );
    return res?.data?.element;
  });

/** The address of the tunnel a sign-up form hands its newly registered client over to, signed in. */
export const requestBookingHandover = async ({
  entityId,
  formId,
  token,
  typeId,
}: {
  entityId: string;
  formId: string;
  token: string;
  /** The type whose page sent the client, which the form's own booking setting does not need to name. */
  typeId?: string | null;
}): Promise<string> => {
  const res = await HttpService.post(
    `/${entityId}/agenda/forms/${formId}/handover`,
    typeId ? { type_id: typeId } : {},
    { headers: { Authorization: token } }
  );
  return res?.data?.element?.url;
};

const registrationEndKey = (entityId: string, formId: string) =>
  `sum:registration-end:${entityId}:${formId}`;

/** The page the sign-up form would have ended on, kept for the tunnel to ask for it at the end. */
export const rememberRegistrationEnd = (
  entityId: string,
  formId: string,
  url: string
) => {
  try {
    sessionStorage.setItem(registrationEndKey(entityId, formId), url);
  } catch {}
};

export const registrationEndOf = (entityId: string, formId: string) => {
  try {
    return sessionStorage.getItem(registrationEndKey(entityId, formId));
  } catch {
    return null;
  }
};

export const forgetRegistrationEnd = (entityId: string, formId: string) => {
  try {
    sessionStorage.removeItem(registrationEndKey(entityId, formId));
  } catch {}
};

export interface RegistrationFinish {
  action: "payment" | "redirect" | "message";
  url: string | null;
}

/** The client's login token, or, back from paying without it, the return link of their booking. */
export type BookingClientProof =
  | { loginToken: string }
  | { bookingReturn: { id: string; sig: string } };

export const useFinishFormRegistration = (entityId?: string) =>
  useMutation<
    RegistrationFinish,
    AxiosError<any>,
    {
      formId: string;
      userId: string;
      proof: BookingClientProof;
      redirectUrl?: string | null;
    }
  >(async ({ formId, userId, proof, redirectUrl }) => {
    const res = await publicHttp.post(
      `/${entityId}/agenda/forms/${formId}/finish/${userId}`,
      {
        ...(redirectUrl ? { redirect_url: redirectUrl } : {}),
        ...("bookingReturn" in proof
          ? { booking_return: proof.bookingReturn }
          : {}),
      },
      "loginToken" in proof
        ? { headers: { "x-login-token": proof.loginToken } }
        : undefined
    );
    return res?.data?.element;
  });
